import { Component, HostBinding } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SearchService } from '@app/core/services/search/base.search.service';
import { BuildActivitiesLogicService } from '@app/logic/build-activities';
import { takeOne } from 'cb-hub-lib';
import { map, Observable, take } from 'rxjs';
import { getBaseFormComponentDirectiveProvider } from '../../../base-form-component';
import {
    AutocompleteScrollerComponent,
    getBaseAutocompleteScrollerProvider
} from '../../autocomplete-scroller.component';
import { ISkinnyBuildActivityDto } from '@classictechsolutions/hubapi-transpiled-enums';

@Component({
    selector: 'cb-build-activity-autocomplete-scroller',
    templateUrl: '../../autocomplete-scroller.component.html',
    providers: [
        ...getBaseFormComponentDirectiveProvider(BuildActivityAutocompleteScrollerComponent),
        getBaseAutocompleteScrollerProvider(BuildActivityAutocompleteScrollerComponent),
        SearchService
    ],
    standalone: false
})
export class BuildActivityAutocompleteScrollerComponent extends AutocompleteScrollerComponent<ISkinnyBuildActivityDto> {
    public results: ISkinnyBuildActivityDto[] = [];
    private resultsLoaded = false;

    @HostBinding('class') class = 'flex-row flex';
    constructor(
        public readonly buildActivityLogic: BuildActivitiesLogicService,
        public readonly dialog: MatDialog,
        public readonly searchService: SearchService
    ) {
        super(dialog, searchService);
        this.searchService.doSearch = this.doSearch;
    }

    // Instead of overriding the doSearch method we could wire up an ElasticSearch handler in the API
    // However, the effort to do so is not worth it at this time for a little gain, fractions of a second faster
    public doSearch = (query: string, page: number): Observable<any[]> => {
        return this.getResults().pipe(
            take(1),
            map(results => {
                const take = 10;
                const skip = page > 0 ? (page - 1) * take : 0;

                // Split query into terms, trim, and lowercase
                const terms = query
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(term => term.length > 0);

                // Filter results: all terms must be found in the name (case-insensitive, partial match)
                const filtered = results.filter(item => {
                    const name = (this.displayWith(item) || '').toLowerCase();
                    return terms.every(term => name.includes(term));
                });

                return filtered.slice(skip, skip + take);
            })
        );
    };

    public displayWith(item: any): string {
        return item ? `${item.code} - ${item.name}` : null;
    }

    /** Reloads results if applicable */
    private getResults(): Observable<ISkinnyBuildActivityDto[]> {
        return new Observable<ISkinnyBuildActivityDto[]>(subscriber => {
            if (!this.resultsLoaded) {
                this.resultsLoaded = true;

                const resultsObservable = this.buildActivityLogic.getSkinnyList();

                resultsObservable
                    .pipe(take(1)).subscribe(x => {
                        this.results = x;
                        subscriber.next(this.results);
                        subscriber.complete();
                    });
            } else {
                subscriber.next(this.results);
                subscriber.complete();
            }
        });
    }
}
